import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { translate } from '@jsverse/transloco';

import { XIColors } from '@inmoment/the-kitchen';

import { DashboardDatasourceFilterService } from 'app/modules/dashboard/services/dashboard-datasource-filter.service';
import { DashboardPillEntitySelectorComponent } from '../dashboard-pill-entity-selector/dashboard-pill-entity-selector.component';
import { MouseMovementState } from '../dashboard-pill-wrapper/pill-hover.directive';
import { AppSharedModule } from '../../app-shared.module';
import { WidgetDataSource } from '../../models';
import {
  ComparisonHandlerService,
  DEFAULT_COMPARISON_COLOR,
} from '../../modules/dashboard/services/comparison-handler-service';
import { DatasourceManagerStore } from '../../modules/dashboard/stores/datasource-manager.store';
import { isDashboardLevelWidgetGroup } from '../../modules/dashboard/stores/widget-group.store';
import { WidgetComponent } from '../../modules/widget/components/widget/widget.component';
import {
  WidgetAnalysisDataSource,
  WidgetAnalysisGroupDataSource,
  WidgetKeyDriverAnalysisDataSource,
} from '../../modules/widget/components/widget-data-source.component';
import { AbstractChartReport } from '../../modules/widget/services/chart-report/chart-report-abstract/chart-report-abstract';
import { HumanizeNumberPipe } from '../../pipes/humanize.pipe';
import {
  Comparison,
  Dashboard,
  Widget,
  WidgetGroup,
} from '../../providers/bridge-api/dashboard.service';
import { Utils } from '../../utils/utils';
import { DashboardPillStateService } from './dashboard-pill-state.service';

export enum ActivityBarPillTypeEnum {
  WIDGET_GROUP = 'WIDGET_GROUP',
  DATA_SOURCE = 'DATA_SOURCE',
  COMPARISON = 'COMPARISON',
}

export enum ActivityBarPillDataSourceTypeEnum {
  ANALYSIS = 'ANALYSIS',
  ANALYSIS_GROUP = 'ANALYSIS_GROUP',
  KEY_DRIVER = 'KEY_DRIVER',
}

export interface ActivityBarInteraction<T> {
  type: ActivityBarPillTypeEnum;
  action:
    | 'changeDataSource'
    | 'addMember'
    | 'bulkEdit'
    | 'changeComparison'
    | 'demote'
    | 'edit'
    | 'apply'
    | 'changeWidgetGroup'
    | 'remove';
  item: T;
  dashboard?: Dashboard;
  widget?: Widget;
}

export interface EntityChange<T> {
  old: T;
  new: T;
}

@Component({
  selector: 'app-dashboard-pill',
  templateUrl: './dashboard-pill.component.html',
  styleUrls: ['./dashboard-pill.component.scss'],
  imports: [AppSharedModule, DashboardPillEntitySelectorComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardPillComponent {
  readonly ActivityBarPillTypeEnum = ActivityBarPillTypeEnum;
  private readonly widgetGroupMenu = viewChild<TemplateRef<unknown>>('widgetGroupMenu');
  private readonly dashboardGroupMenu = viewChild<TemplateRef<unknown>>('dashboardGroupMenu');
  private readonly comparisonGroupMenu = viewChild<TemplateRef<unknown>>('comparisonGroupMenu');

  readonly disabled = input(false);
  readonly isReadOnly = input(false);
  readonly isCondensed = input(true);
  readonly widgetGroup = input<WidgetGroup>();
  readonly dataSource = input<WidgetDataSource | { id: string; isDeleted: boolean }>();
  readonly comparison = input<Comparison>();
  readonly entityList = input<Array<WidgetGroup | Comparison>>();
  readonly activeWidgetCount = input<number>();
  readonly first = input<boolean>();
  readonly last = input<boolean>();
  readonly widget = input<Widget>();
  readonly report = input<AbstractChartReport>();
  readonly dashboard = input<Dashboard>();
  readonly widgetComponent = input<WidgetComponent>();

  readonly onActivitybarInteraction =
    output<
      ActivityBarInteraction<
        | WidgetGroup
        | WidgetDataSource
        | EntityChange<WidgetDataSource | Comparison | WidgetGroup>
        | Comparison
      >
    >();
  readonly entityChooserVisible = output<boolean>();

  private humanizeInt = inject(HumanizeNumberPipe);
  private readonly datasourceManagerStore = inject(DatasourceManagerStore);
  private comparisonService = inject(ComparisonHandlerService);
  private dashboardDatasourceFilterService = inject(DashboardDatasourceFilterService);
  private pillStateService = inject(DashboardPillStateService);

  readonly pillType = computed<ActivityBarPillTypeEnum>(() => {
    if (this.widgetGroup()) {
      return ActivityBarPillTypeEnum.WIDGET_GROUP;
    }
    if (this.dataSource()) {
      return ActivityBarPillTypeEnum.DATA_SOURCE;
    }
    return ActivityBarPillTypeEnum.COMPARISON;
  });

  private readonly filteredOutDataSource = computed(() => {
    const ds = this.dataSource();
    if (!(ds instanceof WidgetDataSource)) {
      return false;
    }
    return this.dashboardDatasourceFilterService.getIsActiveDataSourceFilteredOut(
      ds,
      this.datasourceManagerStore.getDatasources()
    );
  });

  readonly dataSourceType = computed<ActivityBarPillDataSourceTypeEnum>(() => {
    const ds = this.dataSource();
    if (!(ds instanceof WidgetDataSource)) {
      return ActivityBarPillDataSourceTypeEnum.ANALYSIS;
    }
    if ((ds as WidgetAnalysisGroupDataSource).groupId) {
      return ActivityBarPillDataSourceTypeEnum.ANALYSIS_GROUP;
    }
    if (ds.type === 'analysis' && (ds as WidgetKeyDriverAnalysisDataSource).isDriverAnalysis) {
      return ActivityBarPillDataSourceTypeEnum.KEY_DRIVER;
    }
    return ActivityBarPillDataSourceTypeEnum.ANALYSIS;
  });

  readonly icon = computed(() => {
    switch (this.pillType()) {
      case ActivityBarPillTypeEnum.WIDGET_GROUP: {
        const wg = this.widgetGroup();
        if (wg.parentWidget && wg.parentWidget === this.widget()?.id) {
          return 'kxi-icon-split_filled';
        }
        if (isDashboardLevelWidgetGroup(wg)) {
          return 'kxi-icon-community';
        }
        return 'kxi-icon-link';
      }
      case ActivityBarPillTypeEnum.DATA_SOURCE: {
        const ds = this.dataSource();
        if (!(ds instanceof WidgetDataSource)) {
          return 'kxi-icon-link';
        }
        if (this.filteredOutDataSource()) {
          return 'kxi-icon-warning';
        }
        return ds.type === 'group' ? 'kxi-icon-folder' : 'kxi-icon-document_chart';
      }
      case ActivityBarPillTypeEnum.COMPARISON:
        return 'kxi-icon-search_suggestion_filled';
    }
  });

  readonly color = computed(() => {
    switch (this.pillType()) {
      case ActivityBarPillTypeEnum.WIDGET_GROUP: {
        const c = this.widgetGroup().color ?? DEFAULT_COMPARISON_COLOR;
        return `rgba(${c.red},${c.green},${c.blue},${c.alpha})`;
      }
      case ActivityBarPillTypeEnum.DATA_SOURCE:
        if (this.filteredOutDataSource() || !(this.dataSource() instanceof WidgetDataSource)) {
          return XIColors.red.primary as string;
        }
        return XIColors.orca.primary as string;
      case ActivityBarPillTypeEnum.COMPARISON:
        return this.comparison().color;
    }
  });

  readonly textColor = computed(() => (Utils.isLightColor(this.color()) ? '#2d3a44' : 'white'));

  readonly title = computed(() => {
    switch (this.pillType()) {
      case ActivityBarPillTypeEnum.WIDGET_GROUP:
        return this.widgetGroup().title ?? '';
      case ActivityBarPillTypeEnum.DATA_SOURCE: {
        const ds = this.dataSource();
        if (ds instanceof WidgetAnalysisGroupDataSource) {
          return ds.displayName;
        }
        if (ds instanceof WidgetDataSource) {
          return `${ds.collectionDisplayName} (${ds.displayName})`;
        }
        return translate('dashboard-pill.replace-data-source');
      }
      case ActivityBarPillTypeEnum.COMPARISON:
        return this.comparison().title;
    }
  });

  readonly tooltip = computed(() => {
    switch (this.pillType()) {
      case ActivityBarPillTypeEnum.WIDGET_GROUP: {
        const wg = this.widgetGroup();
        const title = wg.title ?? '';
        let parentLabel = '';
        if (wg.parentWidget) {
          parentLabel =
            this.widget()?.id === wg.parentWidget
              ? translate('dashboard-pill.parent-of-widget-group')
              : translate('dashboard-pill.widget-group-has-parent');
        }
        return `${title}
        ${translate('dashboard-pill.widget-group-active-on', {
          widgetNum: wg.widgets.length + (wg.parentWidget ? 1 : 0),
        })}
        ${parentLabel}
        `;
      }
      case ActivityBarPillTypeEnum.DATA_SOURCE: {
        const ds = this.dataSource();
        if (this.filteredOutDataSource()) {
          return translate('dashboard-pill.excluded-data-source');
        }
        if (!(ds instanceof WidgetDataSource)) {
          return `${translate('dashboard-pill.data-source-removed')}
          ${translate('dashboard-pill.widget-group-active-on', { widgetNum: this.activeWidgetCount() })}`;
        }
        return this.assembleDataSourceTooltip(ds);
      }
      case ActivityBarPillTypeEnum.COMPARISON: {
        const c = this.comparison();
        return `${c.title}
        ${translate('dashboard-pill.widget-group-active-on', { widgetNum: c.widgets?.length })}
        ${
          c.filters
            ?.map((f) => this.comparisonService.filterDisplayValueExtractorFn(c.type)(f))
            .join('\n') ?? ''
        }
        `;
      }
    }
  });

  readonly dropdownMenuTemplate = computed<TemplateRef<unknown>>(() => {
    switch (this.pillType()) {
      case ActivityBarPillTypeEnum.DATA_SOURCE:
        return this.dashboardGroupMenu();
      case ActivityBarPillTypeEnum.WIDGET_GROUP:
        return this.widgetGroupMenu();
      case ActivityBarPillTypeEnum.COMPARISON:
        return this.comparisonGroupMenu();
    }
  });

  readonly shouldShowMenu = computed(
    () =>
      !this.disabled() &&
      !(this.isReadOnly() && this.pillType() !== ActivityBarPillTypeEnum.COMPARISON) &&
      this.pillType() !== ActivityBarPillTypeEnum.DATA_SOURCE
  );

  readonly entityChangeDisabled = computed(
    () => this.entityList()?.every((c) => c?.id === this.comparison()?.id) ?? false
  );

  readonly entityChange = signal(false);
  readonly isHovered = signal(false);

  constructor() {
    this.pillStateService.pillHoverState$.pipe(takeUntilDestroyed()).subscribe((state) => {
      this.isHovered.set(
        state.mouseState === MouseMovementState.ENTER &&
          state.entity.id ===
            (this.dataSource()?.id ?? this.widgetGroup()?.id ?? this.comparison()?.id)
      );
    });
  }

  private assembleDataSourceTooltip(
    dataSource:
      | WidgetAnalysisGroupDataSource
      | WidgetKeyDriverAnalysisDataSource
      | WidgetAnalysisDataSource
  ): string {
    const widgetNum = this.activeWidgetCount();
    const docCount = this.humanizeInt.transform(dataSource.documentCount, 1, 1000);
    if (dataSource instanceof WidgetKeyDriverAnalysisDataSource) {
      return `
      ${translate('dashboard-pill.collection')}: ${dataSource.collectionDisplayName}
      ${translate('dashboard-pill.key-driver-analysis')}: ${dataSource.displayName}
      ${translate('dashboard-pill.widget-group-active-on', { widgetNum })} |  ${translate('widgets.numberof-docs', { count: docCount })}`;
    }
    if (dataSource instanceof WidgetAnalysisGroupDataSource) {
      return `
        ${translate('dashboard-pill.analysis-group')}: ${dataSource.displayName}
        ${translate('dashboard-pill.widget-group-active-on', { widgetNum })} |  ${translate('widgets.numberof-docs', { count: docCount })}
      `;
    }
    return `
      ${translate('dashboard-pill.collection')}: ${dataSource.collectionDisplayName}
      ${translate('dashboard-pill.analysis')}: ${dataSource.displayName}
      ${translate('dashboard-pill.widget-group-active-on', { widgetNum })} |  ${translate('widgets.numberof-docs', { count: docCount })}`;
  }

  onMemberEdit() {
    if (this.isReadOnly()) {
      return;
    }

    this.onActivitybarInteraction.emit({
      type: this.pillType(),
      action: 'bulkEdit',
      item: this.widgetGroup(),
    });
  }

  onWidgetGroupRemove() {
    if (this.isReadOnly()) {
      return;
    }

    this.onActivitybarInteraction.emit({
      type: this.pillType(),
      action: 'remove',
      item: this.widgetGroup(),
    });
  }

  onDemote(): void {
    if (this.isReadOnly()) {
      return;
    }

    this.onActivitybarInteraction.emit({
      type: this.pillType(),
      action: 'demote',
      item: this.widgetGroup(),
    });
  }

  onMemberAdd() {
    if (this.isReadOnly()) {
      return;
    }

    this.onActivitybarInteraction.emit({
      type: this.pillType(),
      action: 'addMember',
      item: this.dataSource() as WidgetDataSource,
    });
  }

  onEditComparison(): void {
    if (this.isReadOnly()) {
      return;
    }

    this.onActivitybarInteraction.emit({
      action: 'edit',
      item: this.comparison() as Comparison,
      type: this.pillType(),
    });
  }

  onRemoveComparison(): void {
    if (this.isReadOnly()) {
      return;
    }

    this.onActivitybarInteraction.emit({
      action: 'remove',
      item: this.comparison() as Comparison,
      type: this.pillType(),
    });
  }

  onAddComparisonMembers(): void {
    if (this.isReadOnly()) {
      return;
    }

    this.onActivitybarInteraction.emit({
      action: 'apply',
      item: this.comparison() as Comparison,
      type: this.pillType(),
    });
  }

  onEntitySelect(entity: WidgetDataSource | Comparison | WidgetGroup) {
    if (this.pillType() === ActivityBarPillTypeEnum.DATA_SOURCE) {
      if (this.isReadOnly()) {
        return;
      }

      this.onActivitybarInteraction.emit({
        type: this.pillType(),
        action: 'changeDataSource',
        item: { old: this.dataSource(), new: entity } as EntityChange<WidgetDataSource>,
      });
    }
    if (this.pillType() === ActivityBarPillTypeEnum.COMPARISON) {
      const canSwapComparison = !this.isReadOnly() || !!this.comparison();
      if (this.entityChangeDisabled() || !canSwapComparison) {
        return;
      }
      this.onActivitybarInteraction.emit({
        action: 'changeComparison',
        item: { old: this.comparison(), new: entity as Comparison },
        type: this.pillType(),
      });
    }
    if (this.pillType() === ActivityBarPillTypeEnum.WIDGET_GROUP) {
      if (this.entityChangeDisabled() || this.isReadOnly()) {
        return;
      }
      this.onActivitybarInteraction.emit({
        type: this.pillType(),
        action: 'changeWidgetGroup',
        item: { old: this.widgetGroup(), new: entity } as EntityChange<WidgetGroup>,
      });
    }

    this.showOrHideEntityChange(false);
  }

  onPillClick(): void {
    if (
      this.disabled() ||
      this.isReadOnly() ||
      this.pillType() !== ActivityBarPillTypeEnum.DATA_SOURCE
    ) {
      return;
    }

    this.showOrHideEntityChange(true);
  }

  showOrHideEntityChange(show: boolean) {
    this.entityChange.set(show);
    this.entityChooserVisible.emit(show);
  }
}
